import React, { ReactNode } from 'react';
import { Icon } from '@iwoca/orion';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import { useReviewOffer } from 'components/ReviewOffer/ReviewOffer.context';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';
import { TField } from '../SummaryCard.helpers';
import styles from '../SummaryCard.module.css';
import { Label } from './Label/Label';
import { LabelTooltip } from './Label/LabelTooltip';
import { labels, tooltips } from './SummaryCardLabels.helpers';

type TProps = { fields: TField[] };

export const SummaryCardLabels = ({ fields }: TProps) => {
  const { isUK } = useLocale();
  const { formatPercentage } = useNumberFormat();
  const {
    selectedApplication: {
      status,
      formal_offer_sets,
      preferred_commission_rate,
      commission_eligibility,
    },
  } = useReviewOffer();

  const availableFundsEqualOfferedAmount =
    formal_offer_sets[0]?.formal_offers[0].available_funds ===
    formal_offer_sets[0]?.formal_offers[0].max_amount;

  const isFailedReintroduction =
    isUK &&
    status === APPLICATION_STATUS.offered &&
    commission_eligibility === 'FAILED_REINTRODUCTION';

  // Map labels to components
  const fieldsMap: { [key in TField]?: ReactNode } = {
    max_amount: (
      <div key="max_amount" className={styles.labelContainer}>
        <Label key="max_amount" labelText={labels(status).max_amount} />
        <LabelTooltip
          tooltipText={tooltips(status).max_amount}
          tooltipTestId="offered-amount"
        />
      </div>
    ),
    available_funds: !availableFundsEqualOfferedAmount ? (
      <div key="available_funds" className={styles.labelContainer}>
        <Label key="available_funds" labelText={t`Available funds`} />
        <LabelTooltip tooltipText={tooltips(status).available_funds} />
      </div>
    ) : null,
    interest_rates: (
      <div key="interest_rates" className={styles.labelContainer}>
        <Label key="interest_rates" labelText={t`30 day interest rate`} />
      </div>
    ),
    withdrawal_fee_percentage: (
      <div key="withdrawal_fee_percentage" className={styles.labelContainer}>
        <Label labelText={t`Withdrawal fee`} />
        {isFailedReintroduction && (
          <Icon
            icon="infoCircleFilled"
            fill="var(--color-primary-40)"
            data-testid="failed-reintroduction-warning"
          />
        )}
      </div>
    ),
    total_drawdown_amount: (
      <div key="total_drawdown_amount" className={styles.labelContainer}>
        <Label labelText={t`Withdrawn amount`} />
      </div>
    ),
    initial_commission: (
      <div key="initial_commission" className={styles.labelContainer}>
        <Label labelText="Initial commission" />
        <LabelTooltip
          tooltipText={tooltips(status).initial_commission(
            formatPercentage(preferred_commission_rate),
          )}
          tooltipTestId="initial-commission-tooltip"
        />
      </div>
    ),
    repeat_commission: (
      <div key="repeat_commission" className={styles.labelContainer}>
        <Label labelText="Repeat commission" />
        <LabelTooltip tooltipText={tooltips(status).repeat_commission} />
      </div>
    ),
    monthly_repayments: (
      <div
        key="monthly_repayments"
        className={clsx(styles.labelContainer, styles.monthlyRepaymentsLabel)}
      >
        <Label labelText={t`Monthly repayments`} />
      </div>
    ),
  };

  return (
    <div className={clsx(styles.column, styles.labels)}>
      <div className={styles.termLengthLabel}>
        <p data-testid="term-length">{t`Term length`}</p>
      </div>
      {fields.map((field) => fieldsMap[field])}
    </div>
  );
};
